'use client';
import { useState, useEffect } from 'react';
 import Link from'next/link';
import { usePathname } from 'next/navigation';
 import Icon from'@/components/ui/AppIcon';

const navLinks = [
  { id: 'nav_homepage', label: 'Home', href: '/homepage' },
  { id: 'nav_landscape', label: 'Landscape Map', href: '/s-4-landscape-map' },
  { id: 'nav_functional', label: 'Functional', href: '/functional-playbook' },
  { id: 'nav_technical', label: 'Technical', href: '/technical-playbook' },
  { id: 'nav_migration', label: 'Migration', href: '/migration-paths' },
  { id: 'nav_deployment', label: 'Deployment', href: '/deployment-options' },
  { id: 'nav_frameworks', label: 'Frameworks', href: '/conceptual-frameworks' },
  { id: 'nav_index', label: 'A-Z Index', href: '/a-z-index' },
]

const themes = [
  { id: 'research', label: 'Research', description: 'Dark, serif-heavy' },
  { id: 'systems', label: 'Systems', description: 'Monospace, Linux-manual' },
  { id: 'diagram', label: 'Diagram', description: 'Light, grid-based' },
  { id: 'discussion', label: 'Discussion', description: 'Workshop-friendly' },
]

export default function Header() {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [themePickerOpen, setThemePickerOpen] = useState(false)
  const [currentTheme, setCurrentTheme] = useState('research')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const savedTheme = localStorage.getItem('s4playbook-theme') || 'research'
    setCurrentTheme(savedTheme)
    document.documentElement.setAttribute('data-theme', savedTheme)
  }, [])

  const handleThemeChange = (themeId: string) => {
    setCurrentTheme(themeId)
    document.documentElement.setAttribute('data-theme', themeId)
    localStorage.setItem('s4playbook-theme', themeId)
    setThemePickerOpen(false)
  }

  if (!mounted) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="font-mono text-lg font-semibold">s4playbook.com</div>
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/95 backdrop-blur">
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="font-mono text-lg font-semibold hover:text-primary transition-colors">
            s4playbook.com
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={`text-sm font-mono transition-colors ${
                  pathname === link.href
                    ? 'text-foreground border-b-2 border-primary pb-1'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Theme Picker + Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Theme Picker */}
            <div className="relative">
              <button
                onClick={() => setThemePickerOpen(!themePickerOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded hover:bg-muted transition-colors"
                aria-label="Change theme"
              >
                <Icon name="SwatchIcon" size={16} variant="outline" />
                <span className="hidden sm:inline font-mono text-xs uppercase">{currentTheme}</span>
              </button>

              {themePickerOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded shadow-lg">
                  <div className="p-2">
                    <div className="text-xs font-mono uppercase text-muted-foreground mb-2 px-2">Thinking Mode</div>
                    {themes.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => handleThemeChange(theme.id)}
                        className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted transition-colors ${
                          currentTheme === theme.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="font-mono font-semibold">{theme.label}</div>
                        <div className="text-xs text-muted-foreground">{theme.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-muted rounded transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-border pt-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-sm font-mono ${
                  pathname === link.href ? 'text-foreground font-semibold' : 'text-muted-foreground'
                } hover:text-foreground transition-colors`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  )
}